import { useState, useEffect, useCallback } from 'react';
import { libraryCacheManager } from '../utils/libraryCache';
import { logger } from '../utils/logger';

interface UseLibraryCacheOptions {
  autoCheck?: boolean;
  showDialogOnUpdate?: boolean;
}

interface UseLibraryCacheReturn {
  needsUpdate: boolean;
  isDownloading: boolean;
  showDownloadDialog: boolean;
  setShowDownloadDialog: (show: boolean) => void;
  checkForUpdates: () => void;
  downloadLibraries: () => Promise<boolean>;
}

export const useLibraryCache = (options: UseLibraryCacheOptions = {}): UseLibraryCacheReturn => {
  const { autoCheck = true, showDialogOnUpdate = true } = options;

  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);

  // Check for cache updates
  const checkForUpdates = useCallback(() => {
    try {
      const updateNeeded = libraryCacheManager.needsUpdate();
      setNeedsUpdate(updateNeeded);

      if (updateNeeded && showDialogOnUpdate) {
        setShowDownloadDialog(true);
      }

      logger.log('Library cache update check:', { updateNeeded });
    } catch (error) {
      logger.error('Failed to check for library cache updates:', error);
    }
  }, [showDialogOnUpdate]);

  // Download libraries
  const downloadLibraries = useCallback(async (): Promise<boolean> => {
    try {
      setIsDownloading(true);
      const success = await libraryCacheManager.downloadEssentialLibraries();

      if (success) {
        setNeedsUpdate(false);
        logger.log('Library cache updated successfully');
      }

      return success;
    } catch (error) {
      logger.error('Failed to download libraries:', error);
      return false;
    } finally {
      setIsDownloading(false);
    }
  }, []);

  // Auto-check on mount
  useEffect(() => {
    if (autoCheck) {
      // Delay the check to avoid blocking initial render
      const timer = setTimeout(checkForUpdates, 1000);
      return () => clearTimeout(timer);
    }
  }, [autoCheck, checkForUpdates]);

  return {
    needsUpdate,
    isDownloading,
    showDownloadDialog,
    setShowDownloadDialog,
    checkForUpdates,
    downloadLibraries,
  };
};
